import api from './axios';

/**
 * 계정 분석 지표 — 수집 창구.
 *
 * 레이더를 거치지 않는다. 수집·계산·**저장**이 SaaS 안으로 들어왔다(대표님 지시).
 * 화면은 채널 id(platformId)만 보내고, 서버가 받아서 계산하고 DB 에 쓴다.
 * 그래서 화면이 값을 따로 들고 있을 필요가 없다 — 목록을 다시 부르면 저장된 값이 그대로 뜬다.
 *
 * 주소는 반드시 `/api` 로 시작한다. axios 인스턴스의 baseURL 이 '' 이라서
 * 이 접두어를 빼면 개발서버(CRA)가 그대로 받아 404 를 돌려준다 — 버튼이 조용히 죽는다.
 */

/** 이 수집기가 할 수 있는 플랫폼 — 서버(collect-target.util)와 같은 규칙이어야 한다 */
export const metricsPlatformOf = (row) => {
  const type = String(row?.platform || row?.platformType || '').toLowerCase();
  if (type === 'instagram' || type === 'tiktok') return type;
  /* X 도 같은 수집기로(2026-10-27, 서버 collect-target 과 같은 규칙) */
  if (type === 'x' || type === 'twitter') return 'x';

  const url = String(row?.profileUrl || row || '').toLowerCase();
  if (url.includes('tiktok.com')) return 'tiktok';
  if (url.includes('instagram.com')) return 'instagram';
  if (/(^|\/\/|\.)(x|twitter)\.com\/[a-z0-9_]{1,15}($|[/?#])/.test(url)) return 'x';
  return null;
};

/** 못 하는 플랫폼(유튜브·XHS 등)은 부르지 않는다 — 불러 봐야 실패하는데 호출은 돈이다 */
export const canCollectMetrics = (row) => Boolean(metricsPlatformOf(row));

/* 서버는 응답을 한 겹 더 싼다 — `{ success, data: { ... } }`.
   그 안쪽을 안 벗기면 값이 다 와 있어도 「가져오지 못했습니다」로 읽힌다(실제로 그랬다).
   두 모양 다 받아 준다 — 서버가 포장을 바꿔도 화면이 안 깨지게. */
const unwrap = (body) => (body && body.data !== undefined && body.success !== undefined ? body.data : body);

/**
 * 수집을 기다리는 요청만 따로 길게 잡는다.
 *
 * 공용 axios 는 10초에 끊는다 — 화면 대부분은 그게 맞다. 그런데 수집은 벤더에게
 * 직접 물어보는 길이라 평소 1~2초, 느린 날 30초를 넘긴다(라이브 실측 33.8초).
 * 10초에 끊으면 **벤더 호출은 이미 나갔고 과금도 됐는데** 화면만 실패로 보인다 —
 * 담당자는 다시 누르고, 같은 값을 두 번 산다.
 * 서버가 콜 하나에 60초를 쓰고 인스타는 최대 3콜이라 그 합보다 넉넉히 잡는다.
 */
export const COLLECT_TIMEOUT_MS = 200_000;

const failureMessage = (error) => {
  const status = error?.response?.status;
  if (status === 404) return '이 기능은 아직 서버에 반영되지 않았습니다. 배포 후 동작합니다.';
  if (status === 401 || status === 403) return '이 작업을 할 권한이 없습니다.';
  if (status >= 500) return '서버에서 지표를 가져오지 못했습니다. 잠시 뒤 다시 시도해 주세요.';
  return '지표를 가져오지 못했습니다.';
};

/**
 * 고른 채널의 계정 분석값을 받아 **저장한다.**
 *
 * @param {string[]} platformIds  KOLs DB 행의 platformId
 * @param {{force?: boolean}} options  force 는 사람이 「다시 받기」를 누른 경우에만.
 *        평소에는 최근에 받은 값을 다시 사지 않는다(서버가 막는다).
 * @returns {Promise<{ok:boolean, error?:string, items:Array}>}
 */
export const analyzeCreators = async (platformIds, options = {}) => {
  const ids = [...new Set((platformIds || []).filter(Boolean))];
  if (ids.length === 0) return { ok: true, items: [] };

  try {
    const { data } = await api.post(
      '/api/creator-metrics/analyze',
      { platformIds: ids, force: Boolean(options.force) },
      { timeout: COLLECT_TIMEOUT_MS },
    );

    // 키가 없으면 화면은 기존 값을 그대로 두고 안내만 띄운다 — 빈 값으로 덮지 않는다
    const body = unwrap(data);
    if (body?.ok === false) return { ok: false, error: body.error, items: [] };
    return { ok: true, items: body?.items || [] };
  } catch (error) {
    const e = new Error(failureMessage(error));
    e.status = error?.response?.status ?? null;
    e.cause = error;
    throw e;
  }
};

/**
 * 상세 지표 패널이 쓰는 값 — 저장된 값 + (필요할 때만) 방금 받아 온 값.
 * 패널을 열기만 해도 수집하면 목록을 훑는 동안 계속 돈이 나간다.
 * 그래서 기본은 «저장된 값만»이고, 서버가 «오래됐다»고 볼 때만 새로 받아 온다.
 */
export const getAccountDetail = async (platformId, options = {}) => {
  try {
    const query = options.collect === undefined ? '' : `?collect=${options.collect ? 1 : 0}`;
    // 이 길도 서버가 「오래됐다」고 보면 수집으로 넘어간다 — 같은 이유로 길게 잡는다
    const { data } = await api.get(`/api/creator-metrics/accounts/${platformId}${query}`, {
      timeout: COLLECT_TIMEOUT_MS,
    });
    return unwrap(data);
  } catch (error) {
    const e = new Error(failureMessage(error));
    e.status = error?.response?.status ?? null;
    e.cause = error;
    throw e;
  }
};

/**
 * 계정 한 줄 소개와 AI 성과 요약.
 *
 * **수집을 다시 하지 않는다.** 서버가 이미 저장해 둔 분석값만 읽어 문장을 짓는다 —
 * 벤더 비용은 0원이고 글값만 한 건에 약 3원이다. 같은 수집분이면 서버가 저장해 둔 문장을
 * 그대로 돌려주므로, 창을 여러 번 열어도 한 번만 산다.
 *
 * 실패해도 던지지 않는다. 이건 «있으면 좋은» 문장이라, 못 지었다고 상세 지표 창이
 * 오류로 덮이면 안 된다 — 숫자는 그대로 보여야 한다.
 */
export const getAiSummary = async (platformId, options = {}) => {
  if (!platformId) return { ok: false, message: '' };
  try {
    // 글을 짓는 데 보통 3~8초. 공용 axios 의 10초로는 자주 끊긴다.
    const { data } = await api.post(
      `/api/creator-metrics/accounts/${platformId}/ai-summary`,
      /* lang — 영어·태국어·베트남어 화면이면 서버가 그 언어로 옮겨 준다(2026-10-20 Weglot 단어 한도). 옛 서버는 무시한다 */
      { force: Boolean(options.force), ...(options.lang ? { lang: options.lang } : {}) },
      { timeout: 60_000 },
    );
    const body = unwrap(data);
    if (body?.ok === false) return { ok: false, code: body.code ?? null, message: body.message || '' };
    // translateFailed — 화면 언어로 옮기지 못해 한국어가 왔다(2026-10-28). 화면이 «다시 시도»를 띄운다
    return { ok: true, summary: body?.summary || null, cached: Boolean(body?.cached), translateFailed: body?.translateFailed || null };
  } catch (error) {
    const status = error?.response?.status;
    // 아직 배포 전이면 조용히 접는다 — 없는 기능을 오류로 띄우면 「고장났다」로 읽힌다
    if (status === 404) return { ok: false, code: 'NOT_DEPLOYED', message: '' };
    return { ok: false, code: null, message: '요약을 만들지 못했습니다.' };
  }
};

/** 받아 온 결과를 한 줄 안내문으로 — 「몇 명 가져왔다」만 띄우면 한 명도 못 가져온 날에도 성공처럼 읽힌다 */
export const summarizeAnalysis = (items = []) => {
  const saved = items.filter((i) => i.saved).length;
  const fresh = items.filter((i) => i.skipped === 'fresh').length;
  const inFlight = items.filter((i) => i.skipped === 'in-flight').length;
  /* 「한 번에 받는 수를 넘겨 못 돌았다」는 사람 실수도 벤더 실패도 아니다.
     따로 세지 않으면 «손으로 넣은 값»으로 뭉뚱그려져서, 나눠 보내기가 깨져도 안 보인다. */
  const tooMany = items.filter((i) => i.skipped === 'too-many').length;
  const manual = items.filter(
    (i) => i.skipped && !['fresh', 'in-flight', 'too-many'].includes(i.skipped),
  ).length;
  const failed = items.filter((i) => !i.ok && !i.skipped).length;
  return { saved, fresh, inFlight, manual, tooMany, failed, total: items.length };
};

/** 서버가 한 번에 받는 사람 수 (백엔드 `MAX_TARGETS_PER_CALL` 과 같아야 한다) */
export const ANALYZE_BATCH = 20;

/**
 * 수집이 실패한 이유를 «사람 말»로.
 *
 * 서버는 벤더가 준 코드를 그대로 올려 준다(`CC_404`·`TIMEOUT`·`NO_POSTS` …).
 * 그게 화면에 그대로 뜨면 담당자는 무슨 뜻인지 몰라 **될 때까지 다시 누른다** —
 * 누를 때마다 호출이고 호출은 돈이다. 「다시 눌러 봐야 소용없는 것」과
 * 「잠깐 뒤에 되는 것」을 갈라 주는 게 이 함수가 하는 일이다.
 * (2026-08-10 라이브에서 `CC_404` 가 그대로 떠 있었다)
 */
export const collectErrorText = (code) => {
  const c = String(code || '');
  if (c === 'COLLECTOR_NOT_CONFIGURED') return '수집기에 연결하지 못했습니다. 저장된 값은 그대로 둡니다.';
  if (c === 'UNSUPPORTED_PLATFORM') return '인스타·틱톡이 아니라 지표를 가져올 수 없습니다.';
  if (c === 'CC_404') return '이 계정을 찾지 못했습니다. 아이디가 바뀌었거나 계정이 없어진 것 같습니다 — 프로필 주소를 확인해 주세요.';
  if (c === 'THIN_PROFILE') return '계정은 있는데 팔로워 수를 못 읽었습니다. 비공개 계정일 수 있습니다.';
  if (c === 'NO_POSTS') return '게시물을 하나도 못 읽었습니다. 비공개이거나 올린 글이 없는 계정입니다.';
  if (c === 'TIMEOUT') return '수집기가 제때 답하지 않았습니다. 잠시 뒤 다시 눌러 주세요.';
  if (c === 'CC_429') return '수집기가 잠시 막았습니다(요청이 몰림). 몇 분 뒤 다시 눌러 주세요.';
  if (/^CC_5\d\d$/.test(c)) return '수집기 쪽에 문제가 있습니다. 잠시 뒤 다시 눌러 주세요.';
  if (/^CC_\d+$/.test(c)) return `수집기가 값을 주지 않았습니다 (${c}). 프로필 주소를 확인해 주세요.`;
  if (c === 'VENDOR_ERROR' || !c) return '지표를 가져오지 못했습니다.';
  return `지표를 가져오지 못했습니다 (${c}).`;
};

/**
 * 여러 명을 한 번에 분석하고 **사람에게 보여줄 한 줄까지** 만들어 돌려준다.
 *
 * KOLs DB 와 캠페인 인플루언서 탭이 같은 규칙으로 돌아야 한다 — 화면마다 따로 짜면
 * 「DB에선 됐는데 캠페인에선 안 된다」가 되고, 돈이 걸린 호출이라 그 차이가 요금으로 남는다.
 *
 * 돈이 새지 않게 세 겹으로 막는다.
 *   ⓐ 인스타·틱톡이 아닌 계정은 **아예 안 부른다**(불러 봐야 실패하는데 호출은 돈이다)
 *   ⓑ 최근에 받은 값이면 서버가 건너뛴다. 사람이 「그래도 다시」를 누를 때만 force
 *   ⓒ 끝나면 받음·건너뜀·실패를 그대로 적어 준다 — 「n명 분석했습니다」만 띄우면
 *      한 명도 못 가져온 날에도 성공처럼 읽힌다
 *
 * @param {Array} rows 표의 행들(platform·profileUrl·platformId 를 들고 있는 것)
 * @returns {Promise<{ok, saved, fresh, unsupported, selectionKey, items, message, tone}>}
 *          selectionKey — 「전부 건너뛰었다」를 기억해 두는 열쇠. **같은 사람들을** 한 번 더
 *          누르면 그때 force 를 준다(다른 사람을 골랐는데 다시 사는 일을 막는다).
 */
export const analyzeRows = async (rows, options = {}) => {
  const all = (rows || []).filter(Boolean);
  const collectable = all.filter((it) => canCollectMetrics(it) && it.platformId);
  const unsupported = all.length - collectable.length;
  const platformIds = [...new Set(collectable.map((it) => it.platformId))];
  const selectionKey = [...platformIds].sort().join(',');
  const base = { saved: 0, fresh: 0, unsupported, selectionKey, items: [] };

  if (platformIds.length === 0) {
    return {
      ...base,
      ok: false,
      tone: 'error',
      message: '인스타·틱톡 계정이 없어 지표를 가져올 수 없습니다.',
    };
  }

  /* 서버는 한 번에 20명까지만 받는다. 그보다 많이 보내면 넘친 사람은 «too-many» 로
     되돌아온다 — 예전에는 그것도 없이 조용히 사라졌다(52명 중 20명만 돌고 성공처럼 보였다).
     그래서 화면이 나눠 보낸다. 묶음끼리는 **줄줄이** 보낸다 — 한꺼번에 던지면 벤더 쪽에서
     밀려서 결국 더 느리고, 실패하면 무엇이 나갔는지도 흐려진다.
     onProgress 로 「몇 명째」를 알려 준다. 50명이면 몇 분이 걸릴 수 있어서, 말이 없으면
     담당자가 창을 닫는다 — 닫아도 서버는 계속 사고 있다. */
  const chunks = [];
  for (let i = 0; i < platformIds.length; i += ANALYZE_BATCH) {
    chunks.push(platformIds.slice(i, i + ANALYZE_BATCH));
  }

  const items = [];
  let done = 0;
  for (const chunk of chunks) {
    // eslint-disable-next-line no-await-in-loop
    const res = await analyzeCreators(chunk, { force: Boolean(options.force) });
    if (!res.ok) {
      return {
        ...base,
        ok: false,
        tone: 'error',
        message: res.error === 'COLLECTOR_NOT_CONFIGURED'
          ? '수집기에 연결하지 못했습니다. 기존 값은 그대로 둡니다.'
          : '지표를 가져오지 못했습니다.',
      };
    }
    items.push(...(res.items || []));
    done += chunk.length;
    options.onProgress?.(done, platformIds.length);
  }
  const res = { ok: true, items };

  const sum = summarizeAnalysis(res.items);
  const notes = [
    sum.fresh ? `최근 14일 안에 받은 값 ${sum.fresh}명은 건너뜀` : '',
    sum.manual ? `손으로 넣은 값 ${sum.manual}명은 그대로 둠` : '',
    sum.inFlight ? `${sum.inFlight}명은 지금 다른 사람이 받는 중` : '',
    sum.failed ? `${sum.failed}명은 가져오지 못함` : '',
    /* 나눠 보내므로 평소엔 0 이다. 0 이 아니면 화면과 서버의 묶음 크기가 어긋난 것이다 */
    sum.tooMany ? `${sum.tooMany}명은 한 번에 받는 수를 넘겨 못 돌았음` : '',
    unsupported ? `인스타·틱톡이 아닌 ${unsupported}명은 건너뜀` : '',
  ].filter(Boolean).join(' · ');

  if (sum.saved === 0 && sum.fresh > 0) {
    return {
      ...base, ...sum, unsupported, selectionKey, items: res.items, ok: true, tone: 'info',
      /* 2026-10-21 대표님: 버튼도 «마지막 수집일 기준 2주 후»에만 다시 받는다(크레딧 효율) — «한 번 더 누르면 다시» 는 없앴다 */
      message: '최근 14일 안에 받은 값이라 새로 가져오지 않았습니다. 마지막 수집일로부터 14일이 지나면 다시 받습니다.',
    };
  }
  if (sum.saved === 0) {
    /* 한 명도 못 받았으면 «왜»가 제일 궁금하다. 실패 이유 중 가장 많은 것을 하나 붙여 준다 —
       「가져오지 못했습니다」만 띄우면 담당자는 원인을 모른 채 다시 누른다. */
    const why = res.items.filter((i) => !i.ok && !i.skipped && i.error)
      .map((i) => collectErrorText(i.error))[0];
    return {
      ...base, ...sum, unsupported, selectionKey, items: res.items, ok: false, tone: 'error',
      message: `지표를 새로 가져오지 못했습니다.${why ? ` ${why}` : ''}${notes ? ` (${notes})` : ''}`,
    };
  }
  return {
    ...base, ...sum, unsupported, selectionKey, items: res.items, ok: true, tone: 'success',
    message: `${sum.saved}명의 지표를 새로 저장했습니다.${notes ? ` (${notes})` : ''}`,
  };
};
